// Security monitoring utilities for tracking and analyzing security events
// NOTE: Security events are logged to localStorage only, NOT to GA4, to avoid inflating analytics

export interface SecurityEvent {
  timestamp: number;
  type: string;
  country?: string;
  country_code?: string;
  action: 'allowed' | 'blocked' | 'rate_limited';
  ip?: string;
  user_agent?: string;
  details?: any;
}

const SECURITY_EVENTS_KEY = 'security_events';
const MAX_STORED_EVENTS = 1000;
const EVENT_RETENTION_DAYS = 7;

/**
 * Log a security event to localStorage and analytics
 */
export function logSecurityEvent(event: Omit<SecurityEvent, 'timestamp'>): void {
  const fullEvent: SecurityEvent = {
    ...event,
    timestamp: Date.now(),
  };

  // Store in localStorage (no GA4 tracking - security events should not inflate analytics)
  try {
    const stored = localStorage.getItem(SECURITY_EVENTS_KEY);
    const events: SecurityEvent[] = stored ? JSON.parse(stored) : [];
    
    // Add new event
    events.push(fullEvent);
    
    // Remove events older than retention period
    const retentionMs = EVENT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - retentionMs;
    const filtered = events.filter(e => e.timestamp > cutoff);
    
    // Limit to max stored events
    const trimmed = filtered.slice(-MAX_STORED_EVENTS);
    
    localStorage.setItem(SECURITY_EVENTS_KEY, JSON.stringify(trimmed));
    
    console.log(`🔒 Security event logged: ${event.type} - ${event.action}`);
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
}

/**
 * Get all stored security events
 */
export function getSecurityEvents(): SecurityEvent[] {
  try {
    const stored = localStorage.getItem(SECURITY_EVENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to retrieve security events:', error);
    return [];
  }
}

/**
 * Get security events filtered by criteria
 */
export function getFilteredSecurityEvents(filter: {
  action?: 'allowed' | 'blocked' | 'rate_limited';
  country_code?: string;
  type?: string;
  since?: number;
}): SecurityEvent[] {
  const events = getSecurityEvents();
  
  return events.filter(event => {
    if (filter.action && event.action !== filter.action) return false;
    if (filter.country_code && event.country_code !== filter.country_code) return false;
    if (filter.type && event.type !== filter.type) return false;
    if (filter.since && event.timestamp < filter.since) return false;
    return true;
  });
}

/**
 * Get security statistics
 */
export function getSecurityStats() {
  const events = getSecurityEvents();
  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  const oneDayAgo = now - (24 * 60 * 60 * 1000);
  const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);

  const recentEvents = events.filter(e => e.timestamp > oneHourAgo);
  const dailyEvents = events.filter(e => e.timestamp > oneDayAgo);
  const weeklyEvents = events.filter(e => e.timestamp > oneWeekAgo);

  const blockedCount = events.filter(e => e.action === 'blocked').length;
  const allowedCount = events.filter(e => e.action === 'allowed').length;
  const rateLimitedCount = events.filter(e => e.action === 'rate_limited').length;

  const countries = new Set(events.map(e => e.country_code).filter(Boolean));
  
  const countryBlocks = events
    .filter(e => e.action === 'blocked')
    .reduce((acc, event) => {
      const country = event.country || 'Unknown';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const topBlockedCountries = Object.entries(countryBlocks)
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return {
    total: events.length,
    recentCount: recentEvents.length,
    dailyCount: dailyEvents.length,
    weeklyCount: weeklyEvents.length,
    blocked: blockedCount,
    allowed: allowedCount,
    rateLimited: rateLimitedCount,
    blockRate: events.length > 0 ? (blockedCount / events.length) * 100 : 0,
    uniqueCountries: countries.size,
    topBlockedCountries,
    hourlyRate: dailyEvents.length / 24,
  };
}

/**
 * Clear all security events (admin function)
 */
export function clearSecurityEvents(): void {
  try {
    localStorage.removeItem(SECURITY_EVENTS_KEY);
    console.log('🗑️ Security events cleared');
  } catch (error) {
    console.error('Failed to clear security events:', error);
  }
}

/**
 * Export security events as JSON
 */
export function exportSecurityEvents(): string {
  const events = getSecurityEvents();
  return JSON.stringify(events, null, 2);
}

/**
 * Check if IP is currently rate limited (simplified client-side check)
 */
export function checkClientRateLimit(identifier: string, maxRequests: number, windowMs: number): boolean {
  const key = `rate_limit_${identifier}`;
  
  try {
    const stored = localStorage.getItem(key);
    const data = stored ? JSON.parse(stored) : null;
    
    const now = Date.now();
    
    if (!data || data.resetAt < now) {
      // Create new rate limit window
      localStorage.setItem(key, JSON.stringify({
        count: 1,
        resetAt: now + windowMs
      }));
      return false; // Not rate limited
    }
    
    // Increment count
    data.count++;
    localStorage.setItem(key, JSON.stringify(data));
    
    const isLimited = data.count > maxRequests;
    
    if (isLimited) {
      logSecurityEvent({
        type: 'rate_limit_exceeded',
        action: 'rate_limited',
        details: {
          count: data.count,
          maxRequests,
          resetAt: new Date(data.resetAt).toISOString()
        }
      });
    }
    
    return isLimited;
  } catch (error) {
    console.error('Rate limit check failed:', error);
    return false; // Fail open
  }
}
